// MetricsCard.js - Single metric card for Asset Details Redux (PRD-43)
// Clickable card with label, primary value, sublabel, severity color, optional secondary line

import React from 'react';
import './MetricsCard.css';

/**
 * @param {object} props
 * @param {string} props.label - Top label (e.g. "Scan Score")
 * @param {string|number} props.value - Primary value (e.g. "19")
 * @param {string} [props.sublabel] - Severity label (e.g. "CRITICAL")
 * @param {string} [props.secondary] - Secondary line (e.g. "AI: 78 LOW")
 * @param {'critical'|'high'|'medium'|'low'|'info'|'neutral'} [props.severity] - Severity band
 * @param {string} [props.hint] - Small hint shown under primary value (e.g. "2H 9M 34L")
 * @param {function} [props.onClick] - Click handler. If provided, card becomes clickable.
 * @param {string} [props.ariaLabel] - Accessible label
 * @param {boolean} [props.compact] - Compact variant: ~30% tighter padding + smaller value
 */
function MetricsCard({ label, value, sublabel, secondary, severity = 'neutral', hint, onClick, ariaLabel, compact = false }) {
    const clickable = typeof onClick === 'function';
    const severityClass = severity ? `metrics-card--${severity}` : '';
    const compactClass = compact ? 'metrics-card--compact' : '';

    const handleKeyDown = (e) => {
        if (!clickable) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(e);
        }
    };

    return (
        <div
            className={`metrics-card ${severityClass} ${compactClass} ${clickable ? 'metrics-card--clickable' : ''}`}
            onClick={clickable ? onClick : undefined}
            onKeyDown={handleKeyDown}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={ariaLabel || (clickable ? `${label}: ${value}` : undefined)}
        >
            <div className="metrics-card__label">{label}</div>
            <div className="metrics-card__value">{value}</div>
            {hint && <div className="metrics-card__hint">{hint}</div>}
            {sublabel && <div className="metrics-card__sublabel">{sublabel}</div>}
            {secondary && <div className="metrics-card__secondary">{secondary}</div>}
        </div>
    );
}

export default MetricsCard;
